import { Col, Input, Row } from "antd";
import { useStoreIntegrationMutation } from "api/TenantSlug/Integration/hooks.ts";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { AdvertiserSelectSearchable } from "components/UI/Selects/AdvertiserSelectSearchable/AdvertiserSelectSearchable.tsx";
import { CategorySelectSearchable } from "components/UI/Selects/CategorySelectSearchable/CategorySelectSearchable.tsx";
import { CountrySelectSearchable } from "components/UI/Selects/CountrySelectSearchable/CountrySelectSearchable.tsx";
import { FormStructureSelectSearchable } from "components/UI/Selects/FormStructureSelectSearchable/FormStructureSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser.ts";
import { TOAST_TYPES } from "localConstants.ts";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { IntegrationStatus } from "types/enums";
import { FormId } from "types/models.ts";
import { showCustomToast } from "utils/toast.ts";

import { AddApiIntegrationFormData, schema } from "./formSchema";
import { GeneralFormProvider } from "../../UI/GeneralForm/GeneralFormProvider.tsx";

interface AddApiIntegrationFormProps {
    onSubmit?: () => void;
}

const AddApiIntegrationFormContent = () => {
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { control, fieldNames } = useGeneralForm<AddApiIntegrationFormData>();

    return (
        <div className={"w-100 p-[24px] bg-[#F5F5F5] border border-[#D9D9D9] rounded-[6px] border-dashed"}>
            <Row gutter={[12, 12]}>
                <Col span={12}>
                    <FormItem control={control} name={fieldNames.name.name} label={tFields("name.label")} required>
                        <Input placeholder={tFields("name.placeholder")} />
                    </FormItem>
                </Col>
                <Col span={12}>
                    <FormItem
                        control={control}
                        name={fieldNames.owner_id?.name || "owner_id"}
                        label={tFields("advertiser.label")}
                        required
                    >
                        <AdvertiserSelectSearchable />
                    </FormItem>
                </Col>
            </Row>
            <Row gutter={[12, 12]}>
                <Col span={12}>
                    <FormItem
                        control={control}
                        name={fieldNames.form_structure_id.name}
                        label={tFields("form_structure.label")}
                        required
                    >
                        <FormStructureSelectSearchable />
                    </FormItem>
                </Col>
                <Col span={12}>
                    <FormItem
                        control={control}
                        name={fieldNames.country_id?.name || "country_id"}
                        label={tFields("country.label")}
                    >
                        <CountrySelectSearchable allowClear />
                    </FormItem>
                </Col>
            </Row>
            <Row gutter={[12, 12]}>
                <Col span={12}>
                    <FormItem
                        control={control}
                        name={fieldNames.category?.name || "category"}
                        label={tFields("category.label")}
                    >
                        <CategorySelectSearchable allowClear />
                    </FormItem>
                </Col>
            </Row>
        </div>
    );
};

export const AddApiIntegrationForm: FC<AddApiIntegrationFormProps> = ({ onSubmit }) => {
    const { t } = usePaldockTranslation("forms.add-api-integration");
    const { t: tC } = usePaldockTranslation("common");
    const { getActiveTenant } = useUser();
    const { handleErrorsFromErrorResponse } = useGeneralForm<AddApiIntegrationFormData>();

    const { mutate: createIntegration } = useStoreIntegrationMutation();

    const submit = (values: AddApiIntegrationFormData) => {
        createIntegration(
            { ...values, country_id: values.country_id ?? null, category_id: values.category ?? null },
            {
                onSuccess: (response) => {
                    if (response && response.integration) {
                        showCustomToast(
                            {
                                title: t("messages.success"),
                                buttons: [
                                    {
                                        label: tC("open"),
                                        href: response?.integration?.id
                                            ? `/${getActiveTenant()?.slug_name}/tool/scenario/integration/${response.integration.id}/general`
                                            : undefined,
                                    },
                                ],
                            },
                            TOAST_TYPES.SUCCESS
                        );
                    }
                    onSubmit?.();
                },
                onError: (error) => {
                    toast.error(`Error storing integration: ${error.message}`);
                    handleErrorsFromErrorResponse(error);
                },
            }
        );
    };

    const defaultValues = {
        custom_flow: false,
        debug_mode: false,
        use_new_connection_creator: false,
        form_structure_id: "",
        name: "",
        notes: "",
        status: IntegrationStatus.Creating,
        owner_id: "",
        country_id: null,
        category: null,
    };

    return (
        <GeneralFormProvider<AddApiIntegrationFormData>
            onSubmit={submit}
            id={FormId.AddApiForm}
            schema={schema}
            defaultValues={defaultValues}
        >
            <AddApiIntegrationFormContent />
        </GeneralFormProvider>
    );
};
